// Post-strategy completion messages and returning user messages
// These guide users to existing features via conversational flows

export const STRATEGY_COMPLETE_MESSAGE = `🎉 **Your strategy is ready!**

Here's what I created for you:
✓ Target user & core problem
✓ Key assumptions (these will change — that's normal)
✓ Decisions view (what you're betting on)
✓ Risk areas to watch

---

**Important:** This isn't a document you file away.

Your strategy is a **living thing** that should evolve as you:
- Talk to users and learn what they actually need
- Start building and hit technical realities
- See what competitors do
- Discover new opportunities

Most strategies die because they're created once and forgotten. The good ones get updated as you learn.

---

**What would help you most right now?**

\`\`\`cta_json
[
  {"action":"quick_reply","label":"Start Building","reply_key":"connect_builders_direct","style":"option"},
  {"action":"quick_reply","label":"Create a deliverable","reply_key":"export_intro","style":"option"},
  {"action":"quick_reply","label":"Understand my competitive positioning","reply_key":"competitive_positioning","style":"option"},
  {"action":"quick_reply","label":"Share this with my team/clients","reply_key":"share_with_team","style":"option"},
  {"action":"quick_reply","label":"See how to keep this updated","reply_key":"keep_updated","style":"option"}
]
\`\`\``;

export const POST_STRATEGY_REPLIES = {
  competitive_positioning: `Great question — understanding your competitive positioning is one of the most valuable things you can do right now.

Here's what competitor analysis helps you with:
- **Direct competitors** — who else is solving this problem?
- **Feature gaps** — what do they do that you don't, and vice versa?
- **Positioning opportunities** — where is there white space you can own?
- **Differentiation** — what makes your approach meaningfully different?

The best part: your competitor insights connect back to your living strategy. When you discover something new about competitors, I'll help you decide if your strategy needs to evolve.

\`\`\`cta_json
[
  {"action":"navigate_tab","label":"Run competitor analysis","tabId":"competitor","style":"option"}
]
\`\`\``,

  share_with_team: `Your strategy is shareable in a few ways depending on who you're sharing with:

- **Team members** — give them a live link so they always see the latest version
- **Clients or investors** — share a polished view they can reference
- **Product build** — export as a structured document or README

One thing worth knowing: **the shareable link auto-updates**. So when your strategy evolves (and it will), anyone with the link sees the current version — not the old one.

\`\`\`cta_json
[
  {"action":"open_modal","label":"Get shareable link","modal":"share","style":"option"},
  {"action":"navigate_tab","label":"Export options","tabId":"readme","style":"option"}
]
\`\`\``,

  keep_updated: `There are 3 ways to keep your strategy current as you learn:

**1. Chat editing (right here)**
Just tell me what changed and I'll propose the update before applying it. Examples:
- "Update my target user to..."
- "The core problem has shifted to..."
- "We've decided to drop [assumption]"

**2. Builder connection (for technical teams)**
If you're coding, connect PABS to your tools (Cursor, Replit, Claude Code). Your strategy becomes a live advisor in your editor — no more re-explaining context every session.

**3. Share the current version**
Before things change significantly, share the current version as a snapshot for stakeholders.

Which of these would help you most?

\`\`\`cta_json
[
  {"action":"quick_reply","label":"Show me chat editing examples","reply_key":"show_chat_editing","style":"option"},
  {"action":"quick_reply","label":"Tell me more about builder connection","reply_key":"builders_connection_detail","style":"option"},
  {"action":"quick_reply","label":"I'll come back after I talk to users","reply_key":"reminder_feedback","style":"option"},
  {"action":"quick_reply","label":"I'll come back before I start building","reply_key":"reminder_building","style":"option"}
]
\`\`\``,

  show_chat_editing: `Here are examples of how to update your strategy through chat:

**Updating your target user:**
> "Update my target user to solo founders building B2B SaaS, not enterprise teams"

**Shifting the core problem:**
> "The core problem has changed — it's not discovery anymore, it's retention after the first week"

**Removing an assumption:**
> "We've tested the pricing assumption and it was wrong — update the strategy to reflect that users expect a free tier"

**Adding a new insight:**
> "We found a competitor we missed — add Notion as a competitor and update our differentiation angle"

I'll always show you the proposed change before applying it, so you stay in control.

You can also just describe what you learned and I'll figure out what to update: "I just had 5 user interviews and discovered X" works perfectly.

---
*Prefer editing directly? Click into any field on the **Brief tab** to update it manually.*`,

  connect_builders_direct: `You've done the hard thinking work — the strategy exists. But here's what usually happens next:

You open Cursor (or Replit, or Claude Code) and you start re-explaining your whole context from scratch. Who the user is. What problem you're solving. What you're NOT building and why.

Every. Single. Session.

**PABS as your strategic advisor in your editor** means:
- Your builder always knows the strategy without you explaining it
- When you're about to make a technical decision, PABS can flag if it conflicts with your strategy
- When you discover something new while building, you can update strategy without switching contexts

\`\`\`cta_json
[
  {"action":"open_modal","label":"Yes, connect my builder","modal":"mcp","style":"option"},
  {"action":"quick_reply","label":"Maybe later","reply_key":"maybe_later_mcp","style":"option"},
  {"action":"quick_reply","label":"I'm not using AI coding tools","reply_key":"not_using_builders","style":"option"}
]
\`\`\``,

  builders_connection_detail: `You've done the hard thinking work — the strategy exists. But here's what usually happens next:

You open Cursor (or Replit, or Claude Code) and you start re-explaining your whole context from scratch. Who the user is. What problem you're solving. What you're NOT building and why.

Every. Single. Session.

**PABS as your strategic advisor in your editor** means:
- Your builder always knows the strategy without you explaining it
- When you're about to make a technical decision, PABS can flag if it conflicts with your strategy
- When you discover something new while building, you can update strategy without switching contexts

\`\`\`cta_json
[
  {"action":"open_modal","label":"Yes, connect my builder","modal":"mcp","style":"option"},
  {"action":"quick_reply","label":"Maybe later","reply_key":"maybe_later_mcp","style":"option"},
  {"action":"quick_reply","label":"I'm not using AI coding tools","reply_key":"not_using_builders","style":"option"}
]
\`\`\``,

  reminder_building: `Perfect. Before you start building, come back and let me know what you're planning to build first.

I'll help you check that your strategy still makes sense for that scope — sometimes the first build reveals that one assumption needs updating before you write a line of code.

Your strategy will be here whenever you're ready.`,

  reminder_feedback: `After you talk to users, come back and share what you heard.

Even rough notes work: "I talked to 5 people and they all said X, but nobody cared about Y." I'll help you work out what that means for your strategy and propose any updates.

The strategy should get sharper with every round of feedback — not stay static.`,

  maybe_later_mcp: `No problem. When you're ready to connect, use the top-right **Share** button, then choose a **Build with AI** handoff option like **Handoff to Claude Code**.

It takes about 2 minutes to set up and works with Cursor, Replit, Claude Code, and any other MCP-compatible tool.`,

  not_using_builders: `Got it. Your strategy is still a valuable thinking tool and reference point even without the builder connection.

You can share it with stakeholders, use it to onboard team members, or export it as a document. The strategy is yours to use however makes sense for your workflow.`,

  export_intro: `Your strategy is ready to become a formal deliverable.

Choose the right format depending on who you're sharing with:

- **Product Requirements** — for developers building from your strategy; covers what to build, system behaviour, and technical constraints
- **Statement of Work** — for clients or contractors; covers delivery scope, phases, roles, and effort
- **Venture Brief** — for investors or co-founders; covers market opportunity, business model, and risks

Each is generated from your strategy in one click, then exportable as PDF, Word, or Markdown.

\`\`\`cta_json
[
  {"action":"navigate_tab","label":"Open export tab","tabId":"readme","style":"option"}
]
\`\`\``,
};

/**
 * Builds a contextual welcome-back message for returning users.
 * @param {{ hasCompetitorReport: boolean, isShared: boolean }} context
 * @returns {string} Message with inline cta_json
 */
export const buildReturningUserMessage = ({ hasCompetitorReport, isShared }) => {
  const competitorOption = hasCompetitorReport
    ? `{"action":"navigate_tab","label":"Review my competitor insights","tabId":"competitor","style":"option"}`
    : `{"action":"navigate_tab","label":"Run competitor analysis","tabId":"competitor","style":"option"}`;

  const shareOption = isShared
    ? `{"action":"open_modal","label":"Manage my share link","modal":"share","style":"option"}`
    : `{"action":"open_modal","label":"Share my strategy","modal":"share","style":"option"}`;

  return `Welcome back! Your strategy is a living document — it should evolve as you learn.

**What changed since your last decision?**

\`\`\`cta_json
[
  {"action":"quick_reply","label":"Update what I've learned","reply_key":"show_chat_editing","style":"option"},
  ${competitorOption},
  ${shareOption},
  {"action":"quick_reply","label":"Start building","reply_key":"connect_builders_direct","style":"option"},
  {"action":"quick_reply","label":"Create a deliverable","reply_key":"export_intro","style":"option"}
]
\`\`\``;
};

/**
 * Builds a personalized show_chat_editing message using actual brief content.
 * Falls back to generic examples when fields are empty.
 * @param {Object} planContext - The plan context from EmbeddedChatPanel
 * @returns {string} Personalized chat editing guidance message
 */
export const buildShowChatEditingMessage = (planContext = {}) => {
  const target = planContext?.targetStatement?.trim();
  const problem = planContext?.problemStatement?.trim();

  const targetExample = target
    ? `> "Update my target user — it's not ${target.substring(0, 60)}... anymore, it's [new description]"`
    : `> "Update my target user to solo founders building B2B SaaS, not enterprise teams"`;

  const problemExample = problem
    ? `> "The core problem has shifted — it's not '${problem.substring(0, 60)}...' anymore, it's [new framing]"`
    : `> "The core problem has changed — it's not discovery anymore, it's retention after the first week"`;

  return `Here are examples of how to update your strategy through chat:

**Updating your target user:**
${targetExample}

**Shifting the core problem:**
${problemExample}

**Removing an assumption:**
> "We've tested the pricing assumption and it was wrong — update the strategy to reflect that users expect a free tier"

**Adding a new insight:**
> "We found a competitor we missed — add [competitor name] and update our differentiation angle"

I'll always show you the proposed change before applying it, so you stay in control.

You can also just describe what you learned and I'll figure out what to update: "I just had 5 user interviews and discovered X" works perfectly.

---
*Prefer editing directly? Click into any field on the **Brief tab** to update it manually.*`;
};
